<template>
  <v-dialog
    v-model="visible"
    :fullscreen="isFullscreen"
    max-width="60vw"
    max-height="80vh"
    @keydown.esc="handleEscKey"
    hide-overlay
    persistent
    no-click-animation
    :retain-focus="false"
    :content-class="dialogContentClass"
    :style="dialogStyle"
    :z-index="Z_INDEX.APP_CROPPED_DIALOG"
  >
    <v-card id="cropped-measurements-dialog" :style="cardStyle">
      <!-- Toolbar -->
      <v-toolbar
        dark
        color="primary"
        dense
        class="drag-handle"
        :class="{ 'dragging': isDragging, 'fullscreen': isFullscreen }"
        @mousedown="startDialogDrag"
      >
        <v-icon class="drag-icon mr-2">mdi-drag</v-icon>
        <v-icon class="mr-2">mdi-crop</v-icon>
        <v-toolbar-title>
          Cropped Region Measurements - {{ pointCloudName }}
        </v-toolbar-title>
        <v-spacer></v-spacer>

        <!-- Crop box info -->
        <v-chip small outlined class="mr-2">
          <v-icon left small>mdi-cube-outline</v-icon>
          {{ cropBoxSize }}
        </v-chip>

        <!-- Open in New Tab Button -->
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              @click.stop="openInNewTab"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>mdi-open-in-new</v-icon>
            </v-btn>
          </template>
          <span>Open in New Tab</span>
        </v-tooltip>

        <v-btn icon @click.stop="toggleFullscreen">
          <v-icon>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
        </v-btn>

        <v-btn icon @click.stop="handleClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Error State -->
      <v-card-text v-if="loadingError" class="d-flex flex-column align-center justify-center" style="height: 100%">
        <v-icon color="error" size="80">mdi-alert-circle-outline</v-icon>
        <h2 class="mt-4">Failed to Load Measurement Viewer</h2>
        <p class="text-body-1 mt-2">{{ loadingError.message }}</p>
        <div class="mt-4">
          <v-btn @click="retryLoad" color="primary" class="mr-2">
            <v-icon left>mdi-refresh</v-icon>
            Retry
          </v-btn>
          <v-btn @click="handleClose" outlined>Close</v-btn>
        </div>
      </v-card-text>

      <!-- Main Content -->
      <v-card-text v-else class="pa-0 content-container" :style="contentStyle">
        <!-- Loading State (only covers content area) -->
        <v-overlay :value="isLoading" absolute>
          <div class="text-center d-flex flex-column">
            <v-progress-circular indeterminate size="48" color="primary" />
            <p class="mt-4 white--text">Loading...</p>
          </div>
        </v-overlay>

        <PotreeMeasurementsApp
          v-if="visible"
          ref="potreeApp"
          :point-cloud-url="pointCloudUrl"
          :point-cloud-name="pointCloudName"
          :cylinder-data="cylinderData"
          :corrosion-thresholds="thresholds"
          :corrosion-statistics="statistics"
          :tag-id="tagId"
          @viewer-ready="onViewerReady"
          @pointcloud-ready="onPointCloudReady"
          @potree-error="onPotreeError"
          @create-inspect-tag="handleCreateInspectTag"
          @update-inspect-tag="handleUpdateInspectTag"
        />

        <!-- Point Cloud Loader Button -->
        <PointCloudLoaderButton
          v-if="viewer && !isLoading"
          @toggle="toggleLoaderPanel"
        />

        <!-- Point Cloud Loader Panel -->
        <PointCloudLoaderPanel
          v-if="showLoaderPanel && viewer && cropBox"
          :viewer="viewer"
          :center-point="cropBox.min.clone().add(cropBox.max).divideScalar(2)"
          :point-clouds="$store.state.pointClouds"
          :loaded-point-clouds="loadedPointClouds"
          :loading-point-clouds="loadingPointClouds"
          :failed-point-clouds="failedPointClouds"
          :primary-point-cloud-id="primaryPointCloudId"
          :primary-point-cloud-name="pointCloudName"
          @range-changed="handleRangeChange"
          @close="showLoaderPanel = false"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
const THREE = window.THREE
import PotreeMeasurementsApp from '../PotreeMeasurementsApp.vue';
import PointCloudLoaderButton from '../navigation/PointCloudLoaderButton.vue';
import PointCloudLoaderPanel from '../navigation/PointCloudLoaderPanel.vue';
import {
  convertForgeBoxToPotreeBox,
  createPotreeClipVolume
} from '@/utils/pointCloudCoordinates';
import { Z_INDEX } from '@/utils/zIndexLayers';
import HttpService from '@/utils/httpService';
import { log as debugLog, warn as debugWarn, error as debugError } from '@/utils/debugLogger';

export default {
  name: 'CroppedMeasurementsDialog',

  components: {
    PotreeMeasurementsApp,
    PointCloudLoaderButton,
    PointCloudLoaderPanel
  },

  data() {
    return {
      Z_INDEX,
      isLoading: true,
      loadingStatus: 'Initializing...',
      loadingError: null,
      viewer: null,
      pointcloud: null,
      clipVolume: null,
      pendingCropBox: null,
      isFullscreen: false,
      // Drag state
      isDragging: false,
      dialogPosition: { x: 100, y: 100 },
      dragOffset: { x: 0, y: 0 },
      // Point Cloud Loader state
      showLoaderPanel: false,
      loadedPointClouds: new Map(), // Map<id, {status, pointcloud, distance, name, visible}>
      loadingPointClouds: new Set(), // Set<id>
      failedPointClouds: new Set() // Set<id>
    };
  },

  computed: {
    visible: {
      get() {
        return this.$store.getters.GET_CROPPED_MEASUREMENT_DIALOG.visible;
      },
      set(value) {
        if (!value) {
          this.handleClose();
        }
      }
    },

    dialogData() {
      return this.$store.getters.GET_CROPPED_MEASUREMENT_DIALOG;
    },

    cropBox() {
      return this.dialogData.cropBox;
    },

    primaryPointCloudId() {
      return this.dialogData.pointCloudId;
    },

    pointCloudUrl() {
      return this.dialogData.pointCloudUrl;
    },

    pointCloudName() {
      return this.dialogData.pointCloudName;
    },

    forgePCInstance() {
      return this.dialogData.forgePCInstance;
    },

    unitScale() {
      return this.dialogData.unitScale;
    },

    globalOffset() {
      return this.dialogData.globalOffset;
    },

    modelGlobalOffset() {
      return this.dialogData.modelGlobalOffset;
    },

    analysisResult() {
      return this.dialogData.analysisResult;
    },

    cylinderData() {
      return this.analysisResult?.cylinderData || null;
    },

    thresholds() {
      return this.analysisResult?.thresholds || null;
    },

    statistics() {
      return this.analysisResult?.statistics || null;
    },

    tagId() {
      return this.dialogData.tagId || null;
    },

    cropBoxSize() {
      if (!this.cropBox) return 'N/A';
      // The crop box is stored in viewer-space units; convert to metres for display
      // (metres = viewerUnits × unitScale). Without this the title shows raw units
      // (e.g. "1000.00" for a 1 m box at unitScale=0.001).
      const scale = this.unitScale || this.$store.state.unitScale || 1;
      const size = this.cropBox.getSize(new THREE.Vector3());
      return `${(size.x * scale).toFixed(2)} × ${(size.y * scale).toFixed(2)} × ${(size.z * scale).toFixed(2)} m`;
    },

    contentStyle() {
      if (this.isFullscreen) {
        return 'height: calc(100vh - 48px)';
      }
      return 'height: calc(90vh - 48px)';
    },

    cardStyle() {
      if (this.isFullscreen) {
        return 'height: 100vh';
      }
      return 'height: 90vh';
    },

    dialogStyle() {
      if (this.isFullscreen) {
        return {};
      }
      return {
        position: 'fixed',
        left: `${this.dialogPosition.x}px`,
        top: `${this.dialogPosition.y}px`
      };
    },

    dialogContentClass() {
      const classes = ['cropped-measurements-dialog'];
      if (this.isDragging) {
        classes.push('dragging');
      }
      return classes.join(' ');
    }
  },

  watch: {
    visible(newVal) {
      if (newVal) {
        this.onDialogOpen();
        // Apply initial position to DOM (v-dialog portal workaround)
        this.applyDialogPosition();
      } else {
        this.onDialogClose();
      }
    }
  },

  mounted() {
    // Add event listeners for drag functionality
    document.addEventListener('mousemove', this.handleDialogDrag);
    document.addEventListener('mouseup', this.stopDialogDrag);

    // Load saved dialog position
    this.loadDialogPosition();

    // Apply initial position to DOM
    this.applyDialogPosition();
  },

  methods: {
    onDialogOpen() {
      this.loadingStatus = 'Checking browser support...';

      // Check browser support
      if (!this.checkBrowserSupport()) {
        this.loadingError = new Error('Your browser does not support 3D measurements (WebGL 2 required)');
        this.isLoading = false;
        return;
      }

      // Store crop box for later application
      this.pendingCropBox = this.cropBox;

      debugLog(`%c++++++++++++++++++++++++++++++++++++++++++++++++`, 'font-size:14px; color: red;')
      debugLog('[CroppedMeasurementsDialog] pendingCropBox:', this.pendingCropBox);

      if ((!this.$store.state.viewer || !this.$store.state.viewer.model) && this.modelGlobalOffset) {
        this.$store.state.globalOffset = new THREE.Vector3(this.modelGlobalOffset.x, this.modelGlobalOffset.y, this.modelGlobalOffset.z);
      }
    },

    onDialogClose() {
      debugLog('[CroppedMeasurementsDialog] Dialog closing - checking for corrosion cleanup...');

      // CRITICAL: Disable corrosion visualization before cleanup
      // If corrosion view is enabled, it must be properly disabled to avoid state issues on reopen
      try {
        if (this.$refs.potreeApp && this.$refs.potreeApp.$refs.potreeViewer) {
          const potreeViewer = this.$refs.potreeApp.$refs.potreeViewer;

          // Check if corrosion mode is active
          if (potreeViewer.corrosionMode && potreeViewer.corrosionMode.enabled) {
            debugLog('[CroppedMeasurementsDialog] Corrosion view is active - disabling before cleanup...');
            potreeViewer.disableCorrosionVisualization();
            debugLog('[CroppedMeasurementsDialog] ✅ Corrosion visualization disabled');
          }

          // Also reset corrosion state in PotreeMeasurementsApp
          if (this.$refs.potreeApp.corrosionState && this.$refs.potreeApp.corrosionState.enabled) {
            this.$refs.potreeApp.corrosionState.enabled = false;
            this.$refs.potreeApp.corrosionState.statistics = null;
            debugLog('[CroppedMeasurementsDialog] ✅ PotreeMeasurementsApp corrosion state reset');
          }
        }
      } catch (error) {
        debugError('[CroppedMeasurementsDialog] Error disabling corrosion on close:', error);
        // Continue with cleanup even if corrosion disable fails
      }

      // Cleanup resources
      this.cleanupViewer();

      // Reset loading state for next open
      this.isLoading = true;
      this.loadingStatus = 'Initializing...';
      this.loadingError = null;
    },

    checkBrowserSupport() {
      // Check WebGL 2
      const canvas = document.createElement('canvas');
      const gl = canvas.getContext('webgl2');
      if (!gl) {
        debugError('WebGL 2 not supported');
        return false;
      }

      // Check WebAssembly
      if (typeof WebAssembly === 'undefined') {
        debugError('WebAssembly not supported');
        return false;
      }

      // Check Worker
      if (typeof Worker === 'undefined') {
        debugError('Web Workers not supported');
        return false;
      }

      return true;
    },

    onViewerReady(viewer) {
      this.viewer = viewer;
      this.loadingStatus = 'Viewer ready, waiting for point cloud...';
    },

    handleCreateInspectTag(eventData) {
      debugLog('[CroppedMeasurementsDialog] Forwarding create-inspect-tag event to parent');
      // Forward the event to the parent layout/view
      this.$root.$emit('create-inspect-tag-from-corrosion', eventData);
    },

    handleUpdateInspectTag(eventData) {
      debugLog('[CroppedMeasurementsDialog] Forwarding update-inspect-tag event to parent:', {
        tagId: eventData.tagId
      });
      // Forward the event to the parent layout/view
      this.$root.$emit('update-inspect-tag-from-corrosion', eventData);
    },

    async onPointCloudReady({ viewer, pointcloud }) {
      this.viewer = viewer;
      this.pointcloud = pointcloud;
      this.loadingStatus = 'Applying crop region...';

      // Track primary point cloud - find by name to get ID
      const primaryPC = this.$store.state.pointClouds.find(pc => pc.id === this.primaryPointCloudId);

      if (primaryPC) {
        this.loadedPointClouds.set(primaryPC.id, {
          status: 'loaded',
          pointcloud: this.pointcloud,
          distance: 0,
          isPrimary: true,
          name: primaryPC.name
        });

        debugLog('[PointCloudLoader] Primary point cloud tracked:', {
          id: this.primaryPointCloudId,
          name: this.pointCloudName
        });
      } else {
        debugWarn('[PointCloudLoader] Could not find primary PC in store:', this.pointCloudName);
      }

      try {
        await this.$nextTick();

        if (this.pendingCropBox) {
          await this.applyClippingBox(this.pendingCropBox);
          this.pendingCropBox = null;
        }

        this.isLoading = false;
        this.loadingStatus = 'Ready';

        // First fit to screen to ensure Potree loads visible points after clipping
        if (this.viewer.fitToScreen) {
          debugLog('[CroppedMeasurementsDialog] Fitting to screen before setting back view...');
          this.viewer.fitToScreen();
        }

        // Wait for Potree to update octree and load points
        await this.$nextTick();

        // Short delay to allow Potree's octree to update and points to load
        setTimeout(() => {
          debugLog('[CroppedMeasurementsDialog] Setting back view after fitToScreen...');
          // Now set back view with properly loaded geometry
          this.setBackView();
        }, 300); // 300ms delay for Potree to update

        // REFACTORED: Point cloud loading is now controlled by the PointCloudLoaderPanel
        // The user can adjust the range (0-15m) via the slider in the panel
        // Default range is 0 (no additional point clouds loaded)
        // The saved range preference from localStorage will be applied automatically by the panel
      } catch (error) {
        debugError('[CroppedMeasurementsDialog] ❌ Error during setup:', error);
        this.onPotreeError(error);
      }
    },

    async applyClippingBox(forgeCropBox) {
      debugLog('[🔍 CLIPPING] Starting clipping box application...');

      if (!this.viewer || !this.pointcloud) {
        throw new Error('Viewer or point cloud not ready');
      }

      if (!forgeCropBox || !forgeCropBox.min || !forgeCropBox.max) {
        throw new Error('Invalid crop box: must be a THREE.Box3 with min and max');
      }

      // Transform crop box from Forge space to Potree space
      const unitScale = this.unitScale || this.$store.state.unitScale || 1;
      const globalOffset = this.globalOffset || this.$store.state.globalOffset || new THREE.Vector3(0, 0, 0);

      const potreeBox = convertForgeBoxToPotreeBox(
        forgeCropBox,
        this.forgePCInstance,
        unitScale,
        globalOffset
      );

      // Create Potree clipping volume
      const clipVolume = createPotreeClipVolume(potreeBox, 'outside');

      if (!this.viewer.scene) {
        throw new Error('Potree scene not available');
      }

      // Set clipTask to SHOW_INSIDE
      if (window.Potree && window.Potree.ClipTask) {
        this.viewer.clipTask = window.Potree.ClipTask.SHOW_INSIDE;
      } else {
        this.viewer.clipTask = 2; // SHOW_INSIDE = 2
      }

      // Set clipMethod to INSIDE_ANY
      if (window.Potree && window.Potree.ClipMethod) {
        this.viewer.scene.clipMethod = window.Potree.ClipMethod.INSIDE_ANY;
      }

      // Add volume to scene
      if (this.viewer.scene.addVolume) {
        this.viewer.scene.addVolume(clipVolume);
      } else {
        throw new Error('Potree scene.addVolume method not available');
      }

      this.clipVolume = clipVolume;

      // Trigger viewer update
      if (this.viewer.update) {
        this.viewer.update();
      }

      debugLog('[🔍 CLIPPING] Clipping box applied successfully');
    },

    /**
     * Set back view - position camera at the back of the clip volume
     * This provides a consistent initial view of the cropped region
     */
    setBackView() {
      if (!this.clipVolume) {
        debugWarn('[CroppedMeasurementsDialog] Cannot set back view: clipVolume not available');
        return;
      }

      if (!this.viewer || !this.viewer.scene || !this.viewer.scene.view) {
        debugWarn('[CroppedMeasurementsDialog] Cannot set back view: viewer.scene.view not available');
        return;
      }

      try {
        debugLog('[📷 BACK VIEW] Setting back view for clip volume...');

        // Get clip volume center and size
        const center = new THREE.Vector3(
          this.clipVolume.position.x,
          this.clipVolume.position.y,
          this.clipVolume.position.z
        );

        const size = new THREE.Vector3(
          this.clipVolume.scale.x,
          this.clipVolume.scale.y,
          this.clipVolume.scale.z
        );

        // Calculate distance based on maximum dimension
        const maxDimension = Math.max(size.x, size.y, size.z);
        const distance = maxDimension * 1.5; // Increased distance multiplier for better visibility

        // Back view: camera positioned at negative Y (looking from behind)
        const cameraPosition = new THREE.Vector3(
          center.x,
          center.y - distance, // Looking from behind (negative Y direction)
          center.z
        );

        // Z-axis up vector
        const upVector = new THREE.Vector3(0, 0, 1);

        debugLog('[📷 BACK VIEW] Camera configuration:', {
          center: { x: center.x.toFixed(2), y: center.y.toFixed(2), z: center.z.toFixed(2) },
          cameraPosition: { x: cameraPosition.x.toFixed(2), y: cameraPosition.y.toFixed(2), z: cameraPosition.z.toFixed(2) },
          distance: distance.toFixed(2),
          clipVolumeSize: { x: size.x.toFixed(2), y: size.y.toFixed(2), z: size.z.toFixed(2) }
        });

        // Set the view with smooth animation
        const duration = 500; // 500ms transition
        this.viewer.scene.view.setView(
          cameraPosition,
          center,
          duration,
          () => {
            // Callback after animation completes
            if (this.viewer.setNeedsRedraw) {
              this.viewer.setNeedsRedraw();
            }
            debugLog('[📷 BACK VIEW] ✅ Back view set successfully');
          }
        );

      } catch (error) {
        debugError('[📷 BACK VIEW] ❌ Error setting back view:', error);
        // Fallback to fitToScreen if available
        if (this.viewer.fitToScreen) {
          debugLog('[📷 BACK VIEW] Falling back to fitToScreen');
          this.viewer.fitToScreen();
        }
      }
    },

    onPotreeError(error) {
      debugError('[CroppedMeasurementsDialog] Potree error:', error);
      this.loadingError = error;
      this.isLoading = false;
    },

    retryLoad() {
      debugLog('[CroppedMeasurementsDialog] Retrying load...');
      this.loadingError = null;
      this.isLoading = true;
      this.loadingStatus = 'Retrying...';

      // Force component remount
      this.$nextTick(() => {
        if (this.$refs.potreeApp) {
          // Trigger reload if method exists
          if (this.$refs.potreeApp.reload) {
            this.$refs.potreeApp.reload();
          }
        }
      });
    },

    cleanupViewer() {
      if (!this.viewer) return;

      debugLog('[CroppedMeasurementsDialog] Cleaning up viewer...');

      try {
        // 1. Remove clipping volume and restore clipTask
        if (this.clipVolume && this.viewer.scene && this.viewer.scene.removeVolume) {
          this.viewer.scene.removeVolume(this.clipVolume);
          this.clipVolume = null;

          // Restore clipTask to default (HIGHLIGHT)
          if (window.Potree && window.Potree.ClipTask) {
            this.viewer.clipTask = window.Potree.ClipTask.HIGHLIGHT;
            debugLog('[CroppedMeasurementsDialog] Restored clipTask to HIGHLIGHT');
          }
        }

        // 2. Remove measurements
        if (this.viewer.scene && this.viewer.scene.measurements) {
          const measurements = [...this.viewer.scene.measurements];
          measurements.forEach(m => {
            if (this.viewer.scene.removeMeasurement) {
              this.viewer.scene.removeMeasurement(m);
            }
          });
        }

        // 3. Remove point cloud
        if (this.pointcloud && this.viewer.scene && this.viewer.scene.removePointCloud) {
          this.viewer.scene.removePointCloud(this.pointcloud);
          if (this.pointcloud.dispose) {
            this.pointcloud.dispose();
          }
          this.pointcloud = null;
        }

        // 4. Destroy renderer
        if (this.viewer.renderer) {
          this.viewer.renderer.dispose();

          // Force WebGL context loss
          const gl = this.viewer.renderer.getContext();
          if (gl) {
            const loseContext = gl.getExtension('WEBGL_lose_context');
            if (loseContext) {
              loseContext.loseContext();
            }
          }
        }

        // 5. Remove event listeners
        if (this.viewer.onResize) {
          window.removeEventListener('resize', this.viewer.onResize);
        }

        // 6. Cancel animation frame
        if (this.viewer.rafID) {
          cancelAnimationFrame(this.viewer.rafID);
        }

        // 7. Clear references
        this.viewer = null;

        // 8. Force garbage collection hint
        if (window.gc) {
          setTimeout(() => window.gc(), 100);
        }

        debugLog('[CroppedMeasurementsDialog] Cleanup complete');
      } catch (error) {
        debugError('[CroppedMeasurementsDialog] Error during cleanup:', error);
      }
    },

    handleClose() {
      // Check if there are measurements
      const hasMeasurements = this.viewer && this.viewer.scene && this.viewer.scene.measurements && this.viewer.scene.measurements.length > 0;

      if (hasMeasurements) {
        // Confirm before closing
        if (this.$confirm) {
          this.$confirm(
            'Are you sure you want to close? Measurements will be lost.',
            'Confirm Close',
            {
              confirmButtonText: 'Close',
              cancelButtonText: 'Cancel',
              type: 'warning'
            }
          ).then(() => {
            this.$store.commit('CLEAR_CROPPED_MEASUREMENT_DIALOG');
          }).catch(() => {
            // User cancelled - do nothing
          });
        } else {
          // No confirm dialog available - close anyway
          this.$store.commit('CLEAR_CROPPED_MEASUREMENT_DIALOG');
        }
      } else {
        // No measurements - close immediately
        this.$store.commit('CLEAR_CROPPED_MEASUREMENT_DIALOG');
      }
    },

    handleEscKey() {
      // this.handleClose();
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;

      if (this.isFullscreen) {
        // Reset drag position when entering fullscreen
        this.dialogPosition = { x: 0, y: 0 };

        // CRITICAL: Clear inline styles that were set during dragging
        // These override Vuetify's fullscreen CSS
        this.$nextTick(() => {
          const dialogEl = document.querySelector('.cropped-measurements-dialog');
          if (dialogEl) {
            dialogEl.style.left = '';
            dialogEl.style.top = '';
            dialogEl.style.transform = '';
          }
        });
      } else {
        // Exiting fullscreen - reapply previous position
        this.applyDialogPosition();
      }
    },

    // ========================================
    // Drag Methods
    // ========================================

    /**
     * Start dragging the dialog
     * @param {MouseEvent} event - Mouse event from drag handle
     */
    startDialogDrag(event) {
      // Disable dragging when in fullscreen mode
      if (this.isFullscreen) {
        return;
      }

      // Prevent dragging if clicking on buttons or other interactive elements
      if (event.target.closest('.v-btn') || event.target.closest('button')) {
        return;
      }

      this.isDragging = true;
      this.dragOffset.x = event.clientX - this.dialogPosition.x;
      this.dragOffset.y = event.clientY - this.dialogPosition.y;

      // DIRECTLY set initial DOM position (v-dialog portal issue workaround)
      const dialogEl = document.querySelector('.cropped-measurements-dialog');
      if (dialogEl) {
        dialogEl.style.left = `${this.dialogPosition.x}px`;
        dialogEl.style.top = `${this.dialogPosition.y}px`;
        dialogEl.style.transform = 'none';
      }

      // Prevent text selection and improve dragging experience
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'grabbing';

      // Prevent default behaviors
      event.preventDefault();
      event.stopPropagation();
    },

    /**
     * Handle mouse move during drag
     * @param {MouseEvent} event - Mouse move event
     */
    handleDialogDrag(event) {
      if (!this.isDragging || this.isFullscreen) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const newX = event.clientX - this.dragOffset.x;
      const newY = event.clientY - this.dragOffset.y;

      // Constrain dialog within viewport (absolute positioning)
      const margin = 20;
      const dialogWidth = window.innerWidth * 0.6; // 60vw max-width
      const dialogHeight = window.innerHeight * 0.9; // 90vh height estimate

      const minX = margin;
      const maxX = window.innerWidth - dialogWidth - margin;
      const minY = margin;
      const maxY = window.innerHeight - dialogHeight - margin;

      this.dialogPosition.x = Math.max(minX, Math.min(maxX, newX));
      this.dialogPosition.y = Math.max(minY, Math.min(maxY, newY));

      // DIRECTLY update DOM element (v-dialog portal issue workaround)
      const dialogEl = document.querySelector('.cropped-measurements-dialog');
      if (dialogEl) {
        dialogEl.style.left = `${this.dialogPosition.x}px`;
        dialogEl.style.top = `${this.dialogPosition.y}px`;
        dialogEl.style.transform = 'none';
      }
    },

    /**
     * Stop dragging the dialog
     * @param {MouseEvent} event - Mouse up event
     */
    stopDialogDrag(event) {
      if (this.isDragging) {
        this.isDragging = false;

        // Restore body styles
        document.body.style.userSelect = '';
        document.body.style.cursor = '';

        // Save position to localStorage
        this.saveDialogPosition();

        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    },

    /**
     * Save dialog position to localStorage
     */
    saveDialogPosition() {
      try {
        localStorage.setItem('cropped-measurements-dialog-position', JSON.stringify({
          position: { ...this.dialogPosition }
        }));
      } catch (error) {
        debugWarn('[CroppedMeasurementsDialog] Could not save dialog position:', error);
      }
    },

    /**
     * Load dialog position from localStorage
     */
    loadDialogPosition() {
      try {
        const saved = localStorage.getItem('cropped-measurements-dialog-position');
        if (saved) {
          const { position } = JSON.parse(saved);
          if (position && typeof position.x === 'number' && typeof position.y === 'number') {
            this.dialogPosition = { ...position };
          }
        }
      } catch (error) {
        debugWarn('[CroppedMeasurementsDialog] Could not load dialog position:', error);
      }
    },

    /**
     * Apply dialog position to DOM element
     * Workaround for v-dialog portal issue where :style doesn't apply to portaled element
     */
    applyDialogPosition() {
      this.$nextTick(() => {
        const dialogEl = document.querySelector('.cropped-measurements-dialog');
        if (dialogEl) {
          if (this.isFullscreen) {
            // Clear inline styles for fullscreen mode
            dialogEl.style.left = '';
            dialogEl.style.top = '';
            dialogEl.style.transform = '';
          } else {
            // Apply drag position
            dialogEl.style.left = `${this.dialogPosition.x}px`;
            dialogEl.style.top = `${this.dialogPosition.y}px`;
            dialogEl.style.transform = 'none';
          }
        }
      });
    },

    // ========================================
    // Point Cloud Loader Methods
    // ========================================

    /**
     * Toggle the point cloud loader panel visibility
     */
    toggleLoaderPanel() {
      this.showLoaderPanel = !this.showLoaderPanel;
      debugLog('[PointCloudLoader] Panel toggled:', this.showLoaderPanel);
    },

    /**
     * Handle range change from the loader panel
     * Auto-load point clouds within range, auto-unload point clouds outside range
     */
    async handleRangeChange(newRange) {
      if (!this.viewer || !this.cropBox) {
        debugWarn('[PointCloudLoader] Viewer or cropBox not ready');
        return;
      }

      debugLog(`[PointCloudLoader] Range changed to ${newRange}m`);

      // Calculate crop box center as reference point
      const centerPoint = this.cropBox.min.clone().add(this.cropBox.max).divideScalar(2);

      const pointClouds = this.$store.state.pointClouds ?? [];

      // Calculate which point clouds should be loaded based on distance from crop box center
      // IMPORTANT: Exclude primary PC from distance calculations to prevent accidental unload
      const pcWithDistances = pointClouds
        .filter(pc => pc.id !== this.primaryPointCloudId && pc.pointCloudsScan)  // Exclude primary PC
        .map(pc => {
          // Use the model's pointCloudsScan getter for robust scan lookup
          const scan = pc.pointCloudsScan;
          const distance = scan ? scan.getCameraPositionInViewer().distanceTo(centerPoint) : Infinity;
          return { pc, distance };
        });

      debugLog('[PointCloudLoader] Distance calculations from crop box center (excluding primary):', pcWithDistances);

      // Determine which should be visible (within range)
      const shouldBeVisible = pcWithDistances.filter(({ distance }) => distance <= newRange);
      // Determine which should be hidden (outside range)
      const shouldBeHidden = pcWithDistances.filter(({ distance }) => distance > newRange);

      const toBeVisible = []

      // Show point clouds within range
      for (const { pc, distance } of shouldBeVisible) {
        if (!this.loadedPointClouds.has(pc.id)) {
          // Not loaded yet - load it (will be visible by default)
          if (!this.loadingPointClouds.has(pc.id)) {
            toBeVisible.push(pc);
          }
        } else {
          // Already loaded - just show it if hidden
          const loadedPC = this.loadedPointClouds.get(pc.id);
          if (!loadedPC.visible) {
            this.showPointCloud(pc.id);
          }
        }
      }

      // Hide point clouds outside range (but keep them loaded)
      for (const { pc } of shouldBeHidden) {
        // Extra safety: Skip primary PC (should already be excluded, but double-check)
        if (pc.id === this.primaryPointCloudId) {
          debugWarn('[PointCloudLoader] Skipping primary PC in hide loop');
          continue;
        }

        if (this.loadedPointClouds.has(pc.id)) {
          const loadedPC = this.loadedPointClouds.get(pc.id);
          if (loadedPC.visible) {
            this.hidePointCloud(pc.id);
          }
        }
      }

      // Load point clouds that should be visible with 5 second delay between each load
      for (const pc of toBeVisible) {
        await this.loadPointCloudWithTracking(pc);
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      // Force update to trigger reactivity for Maps/Sets
      this.$forceUpdate();

      // Note: Don't increment loaderPanelKey here - it destroys/recreates the panel
      // and loses the appliedRange state. $forceUpdate() is sufficient for reactivity.
    },

    /**
     * Load a point cloud with status tracking
     */
    async loadPointCloudWithTracking(pc, distance, centerPoint) {
      debugLog(`[PointCloudLoader] Loading ${pc.name} (ID: ${pc.id})...`);

      this.loadingPointClouds.add(pc.id);

      // Fetch signed URLs and initialize PotreeSignedUrls (handles Azure SAS and S3)
      // Pass point cloud name to support multiple point clouds with separate signed URL managers
      const metadataUrl = await window.PotreeSignedUrls.fetchAndInit(pc.url, HttpService, pc.name);

      return new Promise((resolve) => {
        window.Potree.loadPointCloud(metadataUrl, pc.name, (e) => {
          debugLog(`[PointCloudLoader] ✅ Point cloud ${pc.name} (ID: ${pc.id}) loaded successfully`);

          // Update Materials
          if (e.pointcloud && e.pointcloud.material) {
            const material = e.pointcloud.material;
            material.size = 1;
            if (window.Potree.PointSizeType) {
              material.pointSizeType = window.Potree.PointSizeType.ADAPTIVE;
            }
            if (window.Potree.PointShape) {
              material.shape = window.Potree.PointShape.SQUARE;
            }
          }

          // Add to scene
          this.viewer.scene.addPointCloud(e.pointcloud);

          // Ensure point cloud is visible
          e.pointcloud.visible = true;

          // Track success using ID as key
          this.loadedPointClouds.set(pc.id, {
            status: 'loaded',
            pointcloud: e.pointcloud,
            distance: distance,
            name: pc.name,
            visible: true  // Track visibility state
          });
          this.loadingPointClouds.delete(pc.id);
          this.failedPointClouds.delete(pc.id); // Clear any previous failures

          resolve();
        }, (error) => {
          // Handle error
          debugError(`[PointCloudLoader] ❌ Failed to load ${pc.name} (ID: ${pc.id}):`, error);

          this.loadingPointClouds.delete(pc.id);
          this.failedPointClouds.add(pc.id);

          resolve(); // Resolve anyway to continue with other loads
        });
      });
    },

    /**
     * Unload a point cloud from the scene
     */
    unloadPointCloud(pcId) {
      // PROTECTION 1: Check ID match
      if (pcId === this.primaryPointCloudId) {
        debugWarn(`[PointCloudLoader] Cannot unload primary point cloud by ID: ${pcId}`);
        return;
      }

      const loadedPC = this.loadedPointClouds.get(pcId);

      if (!loadedPC) {
        debugWarn(`[PointCloudLoader] Point cloud ID ${pcId} not found in loaded list`);
        return;
      }

      // PROTECTION 2: Check isPrimary flag
      if (loadedPC.isPrimary) {
        debugWarn(`[PointCloudLoader] Cannot unload point cloud marked as primary: ${loadedPC.name || pcId}`);
        return;
      }

      debugLog(`[PointCloudLoader] Unloading ${loadedPC.name || pcId} (ID: ${pcId})...`);

      try {
        // Remove from scene
        if (this.viewer && this.viewer.scene && loadedPC.pointcloud) {

          debugWarn(`[PointCloudLoader] removePointCloud method not available, using fallback`);

          const index = this.viewer.scene.pointclouds.indexOf(loadedPC.pointcloud);
          if (index > -1) {
            this.viewer.scene.pointclouds.splice(index, 1);
            debugLog(`[PointCloudLoader] Removed from pointclouds array at index ${index}`);
          }

          if (this.viewer.scene.scenePointCloud) {
            this.viewer.scene.scenePointCloud.remove(loadedPC.pointcloud);
            debugLog(`[PointCloudLoader] Removed from scenePointCloud`);
          }
        }

        // Remove from tracking
        this.loadedPointClouds.delete(pcId);

        // Force viewer update
        if (this.viewer && this.viewer.update && typeof this.viewer.update === 'function') {
          this.viewer.update();
        }

        debugLog(`[PointCloudLoader] ✅ Point cloud ${loadedPC.name || pcId} successfully unloaded`);
      } catch (error) {
        debugError(`[PointCloudLoader] ❌ Error unloading ${loadedPC.name || pcId}:`, error);
        // Still remove from tracking even if scene removal failed
        this.loadedPointClouds.delete(pcId);
      }
    },

    /**
     * Hide a point cloud by setting its visible property to false
     * The point cloud remains loaded in memory and scene
     */
    hidePointCloud(pcId) {
      const loadedPC = this.loadedPointClouds.get(pcId);

      if (!loadedPC || !loadedPC.pointcloud) {
        debugWarn(`[PointCloudLoader] Cannot hide - point cloud ${pcId} not found in loaded map`);
        return;
      }

      debugLog(`[PointCloudLoader] Hiding ${loadedPC.name || pcId} (ID: ${pcId})`);

      try {
        // Toggle THREE.js visibility
        loadedPC.pointcloud.visible = false;

        // Update tracking state
        loadedPC.visible = false;
        this.loadedPointClouds.set(pcId, loadedPC);

        debugLog(`[PointCloudLoader] ✅ Point cloud ${loadedPC.name || pcId} hidden`);
      } catch (error) {
        debugError(`[PointCloudLoader] ❌ Error hiding ${loadedPC.name || pcId}:`, error);
      }
    },

    /**
     * Show a point cloud by setting its visible property to true
     * The point cloud must already be loaded
     */
    showPointCloud(pcId) {
      const loadedPC = this.loadedPointClouds.get(pcId);

      if (!loadedPC || !loadedPC.pointcloud) {
        debugWarn(`[PointCloudLoader] Cannot show - point cloud ${pcId} not found in loaded map`);
        return;
      }

      debugLog(`[PointCloudLoader] Showing ${loadedPC.name || pcId} (ID: ${pcId})`);

      try {
        // Toggle THREE.js visibility
        loadedPC.pointcloud.visible = true;

        // Update tracking state
        loadedPC.visible = true;
        this.loadedPointClouds.set(pcId, loadedPC);

        debugLog(`[PointCloudLoader] ✅ Point cloud ${loadedPC.name || pcId} shown`);
      } catch (error) {
        debugError(`[PointCloudLoader] ❌ Error showing ${loadedPC.name || pcId}:`, error);
      }
    },

    /**
     * Calculate distance from center point to a point cloud
     */
    calculatePointCloudDistance(pc, centerPoint) {
      try {
        const scans = this.$store.state.projects?.flatMap((p) => p.images ?? []) ?? [];
        const scan = scans.find(s => s.name === pc.name);

        if (!scan) {
          return Infinity;
        }

        const cameraPos = scan.getCameraPositionInViewer();
        return cameraPos.distanceTo(centerPoint);
      } catch (error) {
        debugError(`[PointCloudLoader] Error calculating distance for ${pc.name || pc.id}:`, error);
        return Infinity;
      }
    },

    // ========================================
    // Open in New Tab Functionality
    // ========================================

    /**
     * Open the cropped measurements viewer in a new browser tab
     * Supports two modes:
     * 1. With tagId: Uses database-driven loading (fetches all data from DB)
     * 2. Without tagId (fresh analysis): Stores all data in sessionStorage
     *
     * Route format: /:facilityId/cropped-measurements?tagId=xxx or ?dataKey=xxx
     */
    openInNewTab() {
      debugLog('[CroppedMeasurementsDialog] Opening in new tab...');

      try {
        // Get facilityId from current route params (this is the assetId)
        const facilityId = this.$route?.params?.id;
        if (!facilityId) {
          debugError('[CroppedMeasurementsDialog] Cannot open in new tab - missing facility ID');
          if (this.$message) {
            this.$message.error('Cannot open in new tab - missing facility context');
          }
          return;
        }

        debugLog('[CroppedMeasurementsDialog] Facility ID:', facilityId);

        // Generate a unique key for sessionStorage
        const uniqueKey = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const queryParams = {};

        if (this.tagId) {
          // Mode 1: Existing tag - fetch from DB using tagId
          debugLog('[CroppedMeasurementsDialog] Mode: Existing tag (tagId:', this.tagId, ')');
          queryParams.tagId = this.tagId;
        } else {
          // Mode 2: Fresh analysis - store all data in sessionStorage
          debugLog('[CroppedMeasurementsDialog] Mode: Fresh analysis (no tagId)');

          const dataKey = `cropped-analysis-${uniqueKey}`;

          // Serialize all analysis data needed to reconstruct the view
          // Note: assetId/facilityId is now in route params, not in sessionStorage
          const analysisData = {
            pointCloudUrl: this.pointCloudUrl,
            pointCloudName: this.pointCloudName,
            pointCloudId: this.primaryPointCloudId,
            cropBox: this.cropBox ? {
              min: { x: this.cropBox.min.x, y: this.cropBox.min.y, z: this.cropBox.min.z },
              max: { x: this.cropBox.max.x, y: this.cropBox.max.y, z: this.cropBox.max.z }
            } : null,
            forgePCInstance: this.forgePCInstance ? this.serializeForgePCInstance(this.forgePCInstance) : null,
            unitScale: this.unitScale,
            globalOffset: this.globalOffset ? { x: this.globalOffset.x, y: this.globalOffset.y, z: this.globalOffset.z } : null,
            modelGlobalOffset: this.modelGlobalOffset ? { x: this.modelGlobalOffset.x, y: this.modelGlobalOffset.y, z: this.modelGlobalOffset.z } : null,
            cylinderData: this.cylinderData,
            thresholds: this.thresholds,
            statistics: this.statistics
            // Note: assetId removed - now passed via route param :facilityId
          };

          try {
            sessionStorage.setItem(dataKey, JSON.stringify(analysisData));
            queryParams.dataKey = dataKey;
            debugLog('[CroppedMeasurementsDialog] Stored analysis data in sessionStorage:', dataKey);
          } catch (e) {
            debugError('[CroppedMeasurementsDialog] Failed to store analysis data:', e);
            if (this.$message) {
              this.$message.error('Could not open in new tab - storage may be full');
            }
            return;
          }
        }

        // Serialize current unsaved measurements to sessionStorage (both modes)
        const measurements = this.serializeMeasurements();
        if (measurements.length > 0) {
          const measurementsKey = `cropped-measurements-${uniqueKey}`;
          try {
            sessionStorage.setItem(measurementsKey, JSON.stringify(measurements));
            queryParams.measurementsKey = measurementsKey;
            debugLog(`[CroppedMeasurementsDialog] Stored ${measurements.length} measurements in sessionStorage`);
          } catch (e) {
            debugError('[CroppedMeasurementsDialog] Failed to store measurements:', e);
            if (this.$message) {
              this.$message.warning('Could not transfer measurements - storage may be full');
            }
            // Continue without measurements
          }
        }

        // Generate route URL with id as route param
        const route = this.$router.resolve({
          name: 'CroppedMeasurements',
          params: { id: facilityId },
          query: queryParams
        });

        debugLog('[CroppedMeasurementsDialog] Opening URL:', route.href);

        // Open in new tab
        const newWindow = window.open(route.href, '_blank');

        if (!newWindow) {
          debugError('[CroppedMeasurementsDialog] Failed to open new tab - popup blocker?');
          if (this.$message) {
            this.$message.warning('Please allow popups for this site to open in a new tab');
          }
          return;
        }

        debugLog('[CroppedMeasurementsDialog] Successfully opened in new tab');

      } catch (error) {
        debugError('[CroppedMeasurementsDialog] Error opening in new tab:', error);
        if (this.$message) {
          this.$message.error('Failed to open in new tab');
        }
      }
    },

    /**
     * Serialize forgePCInstance to a JSON-serializable format
     * @param {Object} instance - The forge point cloud instance with THREE.js objects
     * @returns {Object} Serialized instance data
     */
    serializeForgePCInstance(instance) {
      if (!instance) return null;

      const serialized = {
        position: instance.position ? {
          x: instance.position.x,
          y: instance.position.y,
          z: instance.position.z
        } : { x: 0, y: 0, z: 0 },
        quaternion: instance.quaternion ? {
          _x: instance.quaternion._x || instance.quaternion.x || 0,
          _y: instance.quaternion._y || instance.quaternion.y || 0,
          _z: instance.quaternion._z || instance.quaternion.z || 0,
          _w: instance.quaternion._w !== undefined ? instance.quaternion._w : (instance.quaternion.w !== undefined ? instance.quaternion.w : 1)
        } : { _x: 0, _y: 0, _z: 0, _w: 1 },
        scale: instance.scale ? {
          x: instance.scale.x,
          y: instance.scale.y,
          z: instance.scale.z
        } : { x: 1, y: 1, z: 1 }
      };

      // Add pcoGeometry if available
      if (instance.pcoGeometry && instance.pcoGeometry.offset) {
        serialized.pcoGeometry = {
          offset: {
            x: instance.pcoGeometry.offset.x,
            y: instance.pcoGeometry.offset.y,
            z: instance.pcoGeometry.offset.z
          }
        };
      }

      return serialized;
    },

    /**
     * Serialize current measurements to JSON-serializable format
     * Strips THREE.js objects and keeps only essential data
     * @returns {Array} Array of serialized measurements
     */
    serializeMeasurements() {
      if (!this.viewer || !this.viewer.scene || !this.viewer.scene.measurements) {
        return [];
      }

      const measurements = this.viewer.scene.measurements;
      const serialized = [];

      debugLog(`[CroppedMeasurementsDialog] Serializing ${measurements.length} measurements...`);

      for (const measurement of measurements) {
        try {
          // Extract measurement type
          const type = measurement.constructor.name;

          // Common properties
          const serializedMeasurement = {
            type: type,
            name: measurement.name || '',
            showDistances: measurement.showDistances,
            showCoordinates: measurement.showCoordinates,
            showAngles: measurement.showAngles,
            showEdges: measurement.showEdges,
            showArea: measurement.showArea,
            closed: measurement.closed,
            showHeight: measurement.showHeight,
            points: []
          };

          // Serialize points
          if (measurement.points && Array.isArray(measurement.points)) {
            serializedMeasurement.points = measurement.points.map(point => ({
              x: point.position.x,
              y: point.position.y,
              z: point.position.z
            }));
          }

          // Add type-specific properties
          if (type === 'Measure') {
            // Distance, Area, Volume measurements
            serializedMeasurement.closed = measurement.closed;
          } else if (type === 'Angle') {
            // Angle measurements - no special properties
          } else if (type === 'Height') {
            serializedMeasurement.showHeight = measurement.showHeight;
          } else if (type === 'Circle') {
            if (measurement.center) {
              serializedMeasurement.center = {
                x: measurement.center.x,
                y: measurement.center.y,
                z: measurement.center.z
              };
            }
            serializedMeasurement.radius = measurement.radius;
          }

          serialized.push(serializedMeasurement);
          debugLog(`[CroppedMeasurementsDialog] Serialized ${type} measurement with ${serializedMeasurement.points.length} points`);

        } catch (error) {
          debugError('[CroppedMeasurementsDialog] Error serializing measurement:', error);
          // Continue with other measurements
        }
      }

      debugLog(`[CroppedMeasurementsDialog] Successfully serialized ${serialized.length} measurements`);
      return serialized;
    }
  },

  beforeDestroy() {
    // Remove drag event listeners to prevent memory leaks
    document.removeEventListener('mousemove', this.handleDialogDrag);
    document.removeEventListener('mouseup', this.stopDialogDrag);

    // Reset dragging state if active
    if (this.isDragging) {
      this.isDragging = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    }

    this.cleanupViewer();
  }
};
</script>

<style scoped>
/* Ensure full height */
.v-card {
  height: 100%;
}

/* Override any default padding */
.v-card__text {
  padding: 0 !important;
}

/* Content container for relative positioning of overlay */
.content-container {
  position: relative;
}

/* Drag handle styling */
.drag-handle {
  cursor: grab;
  user-select: none;
  transition: background-color 0.2s ease;
}

.drag-handle:not(.fullscreen):hover {
  background-color: var(--v-primary-darken1) !important;
}

.drag-handle:not(.fullscreen):active {
  cursor: grabbing;
}

/* Disable drag cursor in fullscreen */
.drag-handle.fullscreen {
  cursor: default;
}

.drag-handle.dragging {
  cursor: grabbing !important;
}

/* Drag icon styling */
.drag-icon {
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.drag-handle:hover .drag-icon {
  opacity: 1;
}
</style>

<style>
/* Cropped Measurements Dialog - Full-screen point cloud viewer
 * Should be BELOW display settings (2100) but ABOVE all viewer components (max 1500)
 * See: src/utils/zIndexLayers.js for z-index hierarchy
 *
 * NOTE: Positioning is handled via direct DOM manipulation in JavaScript
 * because v-dialog portals the element outside Vue's control
 */
.cropped-measurements-dialog {
  position: fixed !important;
  z-index: var(--z-app-cropped-dialog) !important;
  /* Remove transform - positioning handled by JS */
  will-change: left, top;
  transition: box-shadow 0.2s ease !important;
}

/* Visual feedback during drag */
.cropped-measurements-dialog:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25) !important;
}

/* Active focus ring when dragging */
.cropped-measurements-dialog.dragging {
  cursor: grabbing !important;
  user-select: none;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3) !important;
  outline: 2px solid rgba(33, 150, 243, 0.4);
  outline-offset: 2px;
  transition: none !important; /* Disable transitions during drag */
}

/* Ensure fullscreen mode works regardless of inline styles from dragging */
.cropped-measurements-dialog.v-dialog--fullscreen {
  left: 0 !important;
  top: 0 !important;
  transform: none !important;
  width: 100vw !important;
  height: 100vh !important;
}

/* Vertical Measurement Toolbar - Right Side Position in Dialog */
.cropped-measurements-dialog .measurement-toolbar {
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
}
</style>
